/*!
* # SeriouslyRAD UI
* http://www.seriouslyrad.com/
*
* Copyright (c) 2016 - 2024 Seriously RAD Limited - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*
*/

    "use strict";

    const
        FOCUSABLE_ELEMENTS_SELECTOR = "a, button, input, select, textarea",
        ENABLE_DRAG_DEBUG = false,
        ANIMATION_END_EVENTS = "webkitAnimationEnd oAnimationEnd oanimationend animationend msAnimationEnd",
        TRANSITION_END_EVENTS = "webkitTransitionEnd oTransitionEnd otransitionend transitionend msTransitionEnd";

    // #region Drawing

    // Color
    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "Color",
        properties: [
            { name: "r", type: "int" },
            { name: "g", type: "int" },
            { name: "b", type: "int" },
            { name: "a", type: "float" }
        ],
        createPrototype: namespace =>
        {
            return {
                // Public Methods
                toRgbHexString: function ()
                {
                    return (
                        String.format(
                            "#{0}{1}{2}",
                            (this.rGet() || 0).toString(16).padLeft(2, "0"),
                            (this.gGet() || 0).toString(16).padLeft(2, "0"),
                            (this.bGet() || 0).toString(16).padLeft(2, "0")
                        )
                    );
                },
                toRgbaHexString: function ()
                {
                    return (
                        String.format(
                            "#{0}{1}{2}{3}",
                            (this.rGet() || 0).toString(16).padLeft(2, "0"),
                            (this.gGet() || 0).toString(16).padLeft(2, "0"),
                            (this.bGet() || 0).toString(16).padLeft(2, "0"),
                            ((this.aGet() || 0) * 255).toString(16).padLeft(2, "0")
                        )
                    );
                },
                toRgbaString: function ()
                {
                    return (
                        String.format(
                            "rgba({0}, {1}, {2}, {3})",
                            (this.rGet() || 0),
                            (this.gGet() || 0),
                            (this.bGet() || 0),
                            (this.aGet() || 0)
                        )
                    );
                },
                toRgbString: function ()
                {
                    return (
                        String.format(
                            "rgb({0}, {1}, {2})",
                            (this.rGet() || 0),
                            (this.gGet() || 0),
                            (this.bGet() || 0)
                        )
                    );
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            classConstructor.fromRgbaHexString = s =>
            {
                let
                    rgbaHexString = null;

                if (s.startsWith("#"))
                {
                    switch (s.length)
                    {
                        case 4: // #RGB
                            rgbaHexString = String.format("#{0}{0}{1}{1}{2}{2}FF", s[1], s[2], s[3]);
                            break;
                        case 5: // #RGBA
                            rgbaHexString = String.format("#{0}{0}{1}{1}{2}{2}{3}{3}", s[1], s[2], s[3], s[4]);
                            break;
                        case 7: // #RRGGBB
                            rgbaHexString = String.format("#{0}{1}{2}{3}{4}{5}FF", s[1], s[2], s[3], s[4], s[5], s[6]);
                            break;
                        case 9: // #RRGGBBAA
                            rgbaHexString = s;
                            break;
                    }
                }

                if (!rgbaHexString)
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                const
                    rgba = {
                        r: parseInt(rgbaHexString.substr(1, 2), 16),
                        g: parseInt(rgbaHexString.substr(3, 2), 16),
                        b: parseInt(rgbaHexString.substr(5, 2), 16),
                        a: parseInt(rgbaHexString.substr(7, 2), 16) / 255
                    };

                if (isNaN(rgba.r) || isNaN(rgba.g) || isNaN(rgba.b) || isNaN(rgba.a))
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                return new classConstructor(rgba);
            };
            classConstructor.fromRgbaString = s =>
            {
                let
                    parts = null;

                if (s.startsWith("rgba(", { ignoreCase: true }))
                    parts = s.substr(5, s.length - 6).replace(" ", String.empty).split(",");

                if (!parts || parts.length !== 4)
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                var
                    rgba = {
                        r: parseInt(parts[0]),
                        g: parseInt(parts[1]),
                        b: parseInt(parts[2]),
                        a: parseFloat(parts[3]),
                    };

                if (isNaN(rgba.r) || isNaN(rgba.g) || isNaN(rgba.b) || isNaN(rgba.a))
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                return new classConstructor(rgba);
            };
            classConstructor.fromRgbString = s =>
            {
                var
                    parts = null;

                if (s.startsWith("rgb(", { ignoreCase: true }))
                    parts = s.substr(4, s.length - 5).replace(" ", String.empty).split(",");

                if (!parts || parts.length !== 3)
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                var
                    rgba = {
                        r: parseInt(parts[0]),
                        g: parseInt(parts[1]),
                        b: parseInt(parts[2]),
                        a: 1
                    };

                if (isNaN(rgba.r) || isNaN(rgba.g) || isNaN(rgba.b) || isNaN(rgba.a))
                {
                    console.error(String.format("Not supported: {0}", s));
                    return null;
                }

                return new classConstructor(rgba);
            };
            classConstructor.fromString = s =>
            {
                if (s.startsWith("#"))
                    return classConstructor.fromRgbaHexString(s);
                else if (s.startsWith("rgb(", { ignoreCase: true }))
                    return classConstructor.fromRgbString(s);
                else if (s.startsWith("rgba(", { ignoreCase: true }))
                    return classConstructor.fromRgbaString(s);

                console.error(String.format("Not supported: {0}", s));
                return null;
            };
        }
    });

    // Line
    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "Line",
        createConstructor: namespace =>
        {
            return function (point1, point2)
            {
                this.point1 = point1;
                this.point2 = point2;
            };
        },
        createPrototype: namespace =>
        {
            return {
                // Public Methods
                heading: function ()
                {
                    if (this.point1 === null || this.point2 === null)
                        return null;

                    return this.point1.headingTo(this.point2);
                },
                distance: function ()
                {
                    if (this.point1 === null || this.point2 === null)
                        return 0;

                    return this.point1.distanceTo(this.point2);
                },
                intersectsPoint: function (point)
                {
                    var
                        minX = Math.min(this.point1.x, this.point2.x),
                        maxX = Math.max(this.point1.x, this.point2.x),
                        minY = Math.min(this.point1.y, this.point2.y),
                        maxY = Math.max(this.point1.y, this.point2.y);

                    if (this.point1.y === this.point2.y && this.point1.y === point.y)
                        return point.x >= minX && point.x <= maxX;
                    else if (this.point1.x === this.point2.x && this.point1.x === point.x)
                        return point.y >= minY && point.y <= maxY;

                    return false;
                },
                toCoords: function ()
                {
                    return {
                        x1: this.point1.x,
                        y1: this.point1.y,
                        x2: this.point2.x,
                        y2: this.point2.y
                    };
                },
                toPointList: function ()
                {
                    if (this.point1 === null || this.point2 === null)
                        return null;

                    var
                        x = this.point1.x,
                        y = this.point1.y,
                        pointList = [new namespace.Point(x, y)];

                    while (x !== this.point2.x || y !== this.point2.y)
                    {
                        if (x !== this.point2.x)
                            x += this.point1.x < this.point2.x ? 1 : -1;

                        if (y !== this.point2.y)
                            y += this.point1.y < this.point2.y ? 1 : -1;

                        pointList.push(new namespace.Point(x, y));
                    }

                    return pointList;
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            classConstructor.fromCoords = coords =>
            {
                return new namespace.Line(
                    namespace.Point.fromCoord({ x: coords.x1, y: coords.y1 }),
                    namespace.Point.fromCoord({ x: coords.x2, y: coords.y2 })
                );
            };
        }
    });

    // Point
    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "Point",
        createConstructor: namespace =>
        {
            return function (x, y)
            {
                this.x = x;
                this.y = y;
            };
        },
        createPrototype: namespace =>
        {
            return {
                // Public Methods
                add: function (point)
                {
                    return new namespace.Point(this.x + point.x, this.y + point.y);
                },
                clonePoint: function ()
                {
                    return new namespace.Point(this.x, this.y);
                },
                distanceTo: function (point)
                {
                    return Math.sqrt(Math.pow(point.x - this.x, 2) + Math.pow(point.y - this.y, 2));
                },
                equals: function (point)
                {
                    return (
                        this.x === point.x
                        && this.y === point.y
                    );
                },
                headingTo: function (point)
                {
                    var
                        delta = {
                            x: point.x - this.x,
                            y: this.y - point.y
                        },
                        theta = Math.atan2(delta.x, delta.y),
                        degrees = theta * 180 / Math.PI;

                    if (degrees < 0)
                        degrees = 360 + degrees;

                    return degrees;
                },
                toCoords: function ()
                {
                    return {
                        x: this.x,
                        y: this.y
                    };
                },
                withinBounds: function (rect)
                {
                    return (
                        this.x >= rect.x1
                        && this.x <= rect.x2
                        && this.y >= rect.y1
                        && this.y <= rect.y2
                    );
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            classConstructor.inputTypes = {
                mouse: "mouse",
                touch: "touch"
            };

            classConstructor.fromCoord = coord =>
            {
                return new namespace.Point(coord.x, coord.y);
            };
            classConstructor.fromEvent = (evt, inputType) =>
            {
                if (!inputType)
                    inputType = evt.originalEvent.touches ? classConstructor.inputTypes.touch : classConstructor.inputTypes.mouse;

                switch (inputType)
                {
                    case classConstructor.inputTypes.mouse:
                        return new seriouslyrad.ui.Point(evt.pageX, evt.pageY);
                    case classConstructor.inputTypes.touch:
                        {
                            var input = evt.originalEvent.touches[0] || evt.originalEvent.changedTouches[0];
                            return new seriouslyrad.ui.Point(input.pageX, input.pageY);
                        }
                }

                return null;
            };
        }
    });

    // #endregion

    // #region Helpers

    // Event
    classFactory.create({
        createPrototype: namespace =>
        {
            return {
                // Public Methods
                getInputCoordinates: function (evt)
                {
                    if (evt.type.startsWith("mouse"))
                        return new seriouslyrad.ui.Point(evt.pageX, evt.pageY);
                    else if (evt.type.startsWith("touch"))
                    {
                        var
                            input = evt.originalEvent.touches[0] || evt.originalEvent.changedTouches[0];

                        return new seriouslyrad.ui.Point(input.pageX, input.pageY);
                    }

                    return null;
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            window.eventHelper = new classConstructor();
        }
    });

    // SVG
    classFactory.create({
        createConstructor: namespace =>
        {
            return function ()
            {
                this.svgs = {
                    "empty": $$("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 10 10\"></svg>"),
                    "pip-background": $$("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 10 10\"><circle id=\"Ellipse_6\" data-name=\"Ellipse 6\" cx=\"5\" cy=\"5\" r=\"5\"/></svg>"),
                    "pip-foreground": $$("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 10 10\"><circle id=\"Ellipse_5\" data-name=\"Ellipse 5\" cx=\"5\" cy=\"5\" r=\"5\"/></svg>")
                };
                $$(document).triggerHandler("seriouslyrad.ui.SvgHelper:Initialized", { svgHelper: this });
            }
        },
        createPrototype: namespace =>
        {
            return {
                createSvg: function (key)
                {
                    if (this.svgs && this.svgs[key])
                        return $$(this.svgs[key][0].cloneNode(true)).addClass(key);
                    else
                        return $$(this.svgs["empty"][0].cloneNode(true)).addClass(key);
                },
                registerSvg: function (key, svg)
                {
                    this.svgs[key] = svg;
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            $$(() =>
            {
                window.svgHelper = new classConstructor();
            });
        }
    });

    // #endregion

    // #region Infinite Scroll Manager

    // Infinite Scroll Manager
    classFactory.create({
        namespace: "seriouslyrad.ui.web",
        name: "InfiniteScrollManager",
        properties: [
            { name: "bottomThreshold", type: "int" },
            { name: "$$element" },
            { name: "hasNextResults", type: "boolean" },
            { name: "hasPreviousResults", type: "boolean" },
            { name: "scrollTop", type: "int" }
        ],
        createPropertyDefaults: namespace =>
        {
            return {
                bottomThreshold: 0,
                hasNextResults: true,
                hasPreviousResults: true,
                scrollTop: 0
            };
        },
        createConstructor: namespace =>
        {
            return function ()
            {
                // Trigger Property Changes
                this.$$elementTriggerChange();
            };
        },
        createPrototype: namespace =>
        {
            return {
                // "Protected" Methods
                _$$elementChanged: function (change)
                {
                    if (change.originalValue)
                        change.originalValue.off("scroll", this.onElementScrolled);

                    if (change.currentValue)
                        change.currentValue.on("scroll", { self: this }, this.onElementScrolled);

                    this.scrollTopSet(0);
                },
                _hasNextResultsChanged: function (change)
                {
                    var
                        $$element = this.$$elementGet();

                    if (change.currentValue)
                        $$element.removeClass("rad-state-no-next-results");
                    else
                        $$element.addClass("rad-state-no-next-results");
                },
                _hasPreviousResultsChanged: function (change)
                {
                    var
                        $$element = this.$$elementGet();

                    if (change.currentValue)
                        $$element.removeClass("rad-state-no-previous-results");
                    else
                        $$element.addClass("rad-state-no-previous-results");
                },
                _scrollTopChanged: function (change)
                {
                    var
                        $$element = this.$$elementGet(),
                        scrollTop = change.currentValue || 0,
                        elementScrollTop = Math.round($$element.scrollTop());

                    if (elementScrollTop !== scrollTop)
                        $$element.scrollTop(scrollTop);
                },

                // Public Methods
                restoreScrollPosition: function ()
                {
                    this.$$elementGet().scrollTop(this.scrollTopGet());
                },

                // Event Handlers
                onElementScrolled: function (evt)
                {
                    var
                        self = evt.data.self,
                        $$element = $$(evt.target),
                        scrollTop = Math.round($$element.scrollTop()),
                        location,
                        bottomThreshold = self.bottomThresholdGet();

                    self.scrollTopSet(scrollTop);

                    if (scrollTop === 0)
                        location = "top";
                    else if (scrollTop + Math.round($$element.innerHeight()) >= Math.round($$element[0].scrollHeight) - bottomThreshold)
                        location = "bottom";

                    if (location)
                    {
                        if (location === "top" && !self.hasPreviousResultsGet())
                            return;

                        if (location === "bottom" && !self.hasNextResultsGet())
                            return;

                        var
                            scrollHeight = Math.round($$element[0].scrollHeight);

                        self.triggerHandler(
                            "loadMore",
                            {
                                location: location,
                                callback: function ()
                                {
                                    if (location === "top")
                                        self.scrollTopSet(Math.round($$element[0].scrollHeight) - scrollHeight);
                                }
                            }
                        );
                    }
                }
            };
        }
    });

    // #endregion

    // #region Page Transitioner

    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "PageTransitioner",
        createPrototype: namespace =>
        {
            return {
                // "Protected" Methods
                _animatePageAsync: function (page, options)
                {
                    var
                        animationClass = options.animationName + " " + options.direction + (options.reverse ? " reverse" : String.empty),
                        $$page = page.$$elementGet();

                    $$("body").addClass("rad-state-page-animating");

                    if (!$$page)
                        return Promise.reject();

                    return new Promise(resolve =>
                    {
                        $$page
                            .removeClass(options.animationName + " in out reverse")
                            .on(ANIMATION_END_EVENTS, function ()
                            {
                                $$page.off(ANIMATION_END_EVENTS);
                                $$("body").removeClass("rad-state-page-animating");
                                $$page.removeClass(options.animationName + " in out reverse");

                                if (options.hide)
                                    page.hide();

                                resolve();
                            })
                            .addClass(animationClass);
                    });
                },
                _executePageAnimationAsync: function (pageManager, navigationQueueItem)
                {
                    var
                        self = this,
                        animationName = navigationQueueItem.transitionName,
                        animatedNewPageIn = false,
                        currentPage = pageManager.currentPageGet();

                    if (!animationName)
                        animationName = "bouncy";

                    return new Promise((resolve, reject) =>
                    {
                        self._animatePageAsync(currentPage, { animationName: animationName, direction: "out", reverse: navigationQueueItem.back, hide: true })
                            .then(() =>
                            {
                                navigationQueueItem.$$page.appendTo(pageManager.$$pageContainer);
                                navigationQueueItem.$$page.css("display", String.empty);
                                navigationQueueItem.$$page.addClass("rad-state-invisible");
                                navigationQueueItem.page.show();

                                if (!navigationQueueItem.page[".loaded"])
                                {
                                    return (
                                        navigationQueueItem.page.loadAsync()
                                            .then(() =>
                                            {
                                                navigationQueueItem.page[".loaded"] = true;
                                                return navigationQueueItem.page.activateAsync();
                                            })
                                    );
                                }

                                return navigationQueueItem.page.activateAsync();
                            })
                            .then(() =>
                            {
                                navigationQueueItem.$$page.removeClass("rad-state-invisible");
                                return self._animatePageAsync(navigationQueueItem.page, { animationName: animationName, direction: "in", reverse: navigationQueueItem.back });
                            })
                            .then(() =>
                            {
                                animatedNewPageIn = true;
                                resolve();
                            })
                            .catch(error =>
                            {
                                if (!animatedNewPageIn)
                                {
                                    var
                                        $$currentPage = currentPage.$$elementGet();

                                    if (!$$currentPage)
                                        reject(error);
                                    else
                                    {

                                        navigationQueueItem.$$page.css("display", "none");
                                        navigationQueueItem.$$page.addClass("rad-state-invisible");
                                        navigationQueueItem.page.hide();

                                        $$currentPage.appendTo(pageManager.$$pageContainer);
                                        $$currentPage.css("display", String.empty);
                                        $$currentPage.removeClass("rad-state-invisible");
                                        currentPage.show();

                                        self._animatePageAsync(currentPage, { animationName: animationName, direction: "in", reverse: true })
                                            .finally(() =>
                                            {
                                                reject(error);
                                            });
                                    }

                                    return;
                                }

                                reject(error);
                            });
                    });
                },
                _executePageTransitionAsync: function (pageManager, navigationQueueItem)
                {
                    switch (navigationQueueItem.transitionName)
                    {
                        case "fade":
                            return this._executePageTransitionFadeAsync(pageManager, navigationQueueItem);
                        case "slide":
                            return this._executePageTransitionSlideAsync(pageManager, navigationQueueItem);
                        default:
                            return this._executePageTransitionDefaultAsync(pageManager, navigationQueueItem);
                    }
                },
                _executePageTransitionDefaultAsync: function (pageManager, navigationQueueItem)
                {
                    var
                        currentPage = pageManager.currentPageGet(),
                        $$currentPage = currentPage.$$elementGet(),
                        pageLoadAndOrActivatePromise;

                    navigationQueueItem.$$page.appendTo(pageManager.$$pageContainer);
                    navigationQueueItem.$$page.css("display", String.empty);
                    navigationQueueItem.$$page.addClass("rad-state-invisible");
                    navigationQueueItem.page.show();

                    if (!navigationQueueItem.page[".loaded"])
                    {
                        pageLoadAndOrActivatePromise =
                            navigationQueueItem.page.loadAsync()
                                .then(() =>
                                {
                                    navigationQueueItem.page[".loaded"] = true;
                                    return navigationQueueItem.page.activateAsync();
                                });
                    }
                    else
                        pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

                    return (
                        pageLoadAndOrActivatePromise
                            .then(() =>
                            {
                                $$currentPage.css("display", "none");
                                currentPage.hide();

                                navigationQueueItem.$$page.removeClass("rad-state-invisible");
                            })
                            .catch(_ =>
                            {
                                navigationQueueItem.$$page.css("display", "none");
                                navigationQueueItem.$$page.addClass("rad-state-invisible");
                                navigationQueueItem.page.hide();

                                $$currentPage.appendTo(pageManager.$$pageContainer);
                                $$currentPage.css("display", String.empty);
                                $$currentPage.removeClass("rad-state-invisible");
                                currentPage.show();

                                throw error;
                            })
                    );
                },
                _executePageTransitionFadeAsync: function (pageManager, navigationQueueItem)
                {
                    return new Promise((resolve, reject) =>
                    {
                        const
                            currentPage = pageManager.currentPageGet(),
                            $$currentPage = currentPage.$$elementGet();

                        let
                            pageLoadAndOrActivatePromise;

                        navigationQueueItem.$$page.appendTo(pageManager.$$pageContainer);
                        navigationQueueItem.$$page.css("display", String.empty);
                        navigationQueueItem.$$page.css({ opacity: 0, transition: String.empty });
                        navigationQueueItem.page.show();

                        $$currentPage.css({ opacity: 1 });

                        // Force repaint
                        pageManager.$$pageContainer[0].offsetHeight;

                        function currentPageTransitionEnd(evt)
                        {
                            if (evt.target !== this)
                                return;

                            $$currentPage.off(TRANSITION_END_EVENTS, currentPageTransitionEnd);

                            $$currentPage.css({
                                display: "none",
                                opacity: String.empty,
                                transition: String.empty
                            });

                            currentPage.hide();

                            navigationQueueItem.$$page.on(TRANSITION_END_EVENTS, newPageTransitionEnd);
                            navigationQueueItem.$$page.css({ transition: "opacity 0.25s ease-out" });
                            setTimeout(() => { navigationQueueItem.$$page.css({ opacity: 1 }); });
                        }

                        function newPageTransitionEnd(evt)
                        {
                            if (evt.target !== this)
                                return;

                            navigationQueueItem.$$page.off(TRANSITION_END_EVENTS, newPageTransitionEnd);

                            navigationQueueItem.$$page.css({ opacity: String.empty, transition: String.empty });
                            resolve();
                        }

                        if (!navigationQueueItem.page[".loaded"])
                        {
                            pageLoadAndOrActivatePromise =
                                navigationQueueItem.page.loadAsync()
                                    .then(() =>
                                    {
                                        navigationQueueItem.page[".loaded"] = true;
                                        return navigationQueueItem.page.activateAsync();
                                    });
                        }
                        else
                            pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

                        pageLoadAndOrActivatePromise
                            .then(() =>
                            {
                                $$currentPage.on(TRANSITION_END_EVENTS, currentPageTransitionEnd);
                                $$currentPage.css({ transition: "opacity 0.25s ease-out" });
                                setTimeout(() => { $$currentPage.css({ opacity: 0 }); });
                            })
                            .catch(error =>
                            {
                                navigationQueueItem.$$page.css("display", "none");
                                navigationQueueItem.page.hide();

                                $$currentPage.appendTo(pageManager.$$pageContainer);
                                $$currentPage.css({ display: String.empty });
                                currentPage.show();

                                pageManager.$$pageContainer
                                    .css({
                                        left: "0px",
                                        transition: String.empty
                                    });

                                reject(error);
                            });
                    });
                },
                _executePageTransitionSlideAsync: function (pageManager, navigationQueueItem)
                {
                    return new Promise((resolve, reject) =>
                    {
                        const
                            currentPage = pageManager.currentPageGet(),
                            $$currentPage = currentPage.$$elementGet();

                        let
                            pageLoadAndOrActivatePromise;

                        if (navigationQueueItem.back)
                            navigationQueueItem.$$page.insertBefore($$currentPage);
                        else
                            navigationQueueItem.$$page.appendTo(pageManager.$$pageContainer);

                        navigationQueueItem.$$page.css("display", String.empty);
                        navigationQueueItem.page.show();

                        pageManager.$$pageContainer
                            .css({
                                left: navigationQueueItem.back ? "-100%" : "0%",
                                transition: String.empty
                            });

                        // Force repaint
                        pageManager.$$pageContainer[0].offsetHeight;

                        function transitionEnd(evt)
                        {
                            if (evt.target !== this)
                                return;

                            pageManager.$$pageContainer.off(TRANSITION_END_EVENTS, transitionEnd);

                            $$currentPage.css("display", "none");
                            currentPage.hide();

                            if (navigationQueueItem.back)
                                navigationQueueItem.$$page.appendTo(pageManager.$$pageContainer);

                            pageManager.$$pageContainer
                                .css({
                                    left: "0%",
                                    transition: String.empty
                                });

                            resolve();
                        }

                        if (!navigationQueueItem.page[".loaded"])
                        {
                            pageLoadAndOrActivatePromise =
                                navigationQueueItem.page.loadAsync()
                                    .then(() =>
                                    {
                                        navigationQueueItem.page[".loaded"] = true;
                                        return navigationQueueItem.page.activateAsync();
                                    });
                        }
                        else
                            pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

                        pageLoadAndOrActivatePromise
                            .then(() =>
                            {
                                pageManager.$$pageContainer
                                    .css({ transition: "left 0.25s ease-out" })
                                    .on(TRANSITION_END_EVENTS, transitionEnd)
                                    .css({ left: navigationQueueItem.back ? "0%" : "-100%" });
                            })
                            .catch(error =>
                            {
                                navigationQueueItem.$$page.css("display", "none");
                                navigationQueueItem.page.hide();

                                $$currentPage.appendTo(pageManager.$$pageContainer);
                                $$currentPage.css("display", String.empty);
                                currentPage.show();

                                pageManager.$$pageContainer
                                    .css({
                                        left: "0%",
                                        transition: String.empty
                                    });

                                reject(error);
                            });
                    });
                },

                // Public Methods
                executeAsync: function (pageManager, navigationQueueItem)
                {
                    switch (navigationQueueItem.transitionType)
                    {
                        case "animation":
                            return this._executePageAnimationAsync(pageManager, navigationQueueItem);
                        case "transition":
                            return this._executePageTransitionAsync(pageManager, navigationQueueItem);
                        default:
                            return this._executePageAnimationAsync(pageManager, navigationQueueItem);
                    }
                }
            };
        }
    });

    // #endregion

    // #region Page Manager

    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "PageManager",
        properties: [
            { name: "authenticationData" },
            { name: "currentPage" },
            { name: "maximumNumberOfPages", type: "int" },
            { name: "navigationSuspended", type: "boolean" },
            { name: "onAuthenticated", type: "function" },
            { name: "onRequestAuthenticationData", type: "function" },
            { name: "onUnauthenticated", type: "function" },
            { name: "pageTransitioner" },
            { name: "scrollBarDimensions" }
        ],
        createPropertyDefaults: namespace =>
        {
            return {
                maximumNumberOfPages: 20,
                navigationSuspended: false,
                pageTransitioner: new namespace.PageTransitioner()
            };
        },
        createConstructor: namespace =>
        {
            return function ()
            {
                this._initializationPromises = [];
                this._blockNavigationFunctions = [];
                this._navigationHandlers = [];
                this._navigationQueue = [];
                this._processingNavigationQueue = false;

                this.progressLoader = new namespace.web.FullPageLoader({ mode: "progress", total: 20, text: "{left} of {total} items remaining..." });

                this._init();
            };
        },
        createPrototype: namespace =>
        {
            return {
                // "Protected" Methods
                _addAuthenticationHeaders: function (headers)
                {
                    const
                        onRequestAuthenticationData = this.onRequestAuthenticationDataGet(),
                        authenticationData = onRequestAuthenticationData ? onRequestAuthenticationData() : null;

                    if (authenticationData)
                    {
                        switch (authenticationData.scheme)
                        {
                            case "Cookies":
                                headers["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                                break;
                            case "Bearer":
                                headers["Authorization"] = String.format("Bearer {0}", authenticationData.accessToken);
                                break;
                        }
                    }
                },
                _backupTabIndexesIfPageDimmed: function ($$page)
                {
                    /* TODO: This probably wants to be done by each Control,
                       but then there's child controls to consider as we only
                       want to run once per control + children initialization
                    
                       *Because performance. */

                    if (!$$(document.body).hasClass("rad-state-dimmed"))
                        return;

                    /* INFO: If we're dimmed, the tabindex attributes are in a backup state.
                       We need to save the tabindex to the backup attribute and set the
                       tabindex to -1 */

                    $$page
                        .add($$page.find("[tabindex]"))
                        .forEach(item =>
                        {
                            item.setAttribute("data-tabindex-backup", item.getAttribute("tabindex"));
                            item.setAttribute("tabindex", -1);
                        });
                },
                _removeOldPages: function ()
                {
                    var
                        maximumNumberOfPages = this.maximumNumberOfPagesGet(),
                        numberOfPages = this.$$pageContainer.children().length;

                    while (numberOfPages > maximumNumberOfPages)
                    {
                        this._removePageByElement(this.$$pageContainer.children().first());
                        numberOfPages--;
                    }
                },
                _removePageByElement: function ($$page)
                {
                    var
                        page = $$page.radControl();

                    if (page)
                        page.remove();
                    else
                        $$page.remove();
                },
                _removePageByUrl: function (url)
                {
                    var
                        page = this._getPageByUrl(url);

                    if (page)
                        page.remove();
                },
                _featureDetectScrollbars: function ()
                {
                    var
                        $$div = $$.create$$({ nodeName: "div" }).css({ "height": "100px", "overflow": "scroll", "width": "100px" }),
                        $$innerDiv = $$.create$$({ nodeName: "div" }).css({ "height": "100%", "width": "100%" });

                    $$div
                        .append($$innerDiv)
                        .appendTo(document.body);

                    var
                        scrollBarDimensions = {
                            height: $$div[0].offsetHeight - $$div[0].clientHeight,
                            width: $$div[0].offsetWidth - $$div[0].clientWidth
                        };

                    this.scrollBarDimensionsSet(scrollBarDimensions);

                    $$("html").addClass(
                        scrollBarDimensions.width > 0
                            ? "rad-feature-scrollbars-yes"
                            : "rad-feature-scrollbars-no"
                    );

                    $$div.remove();
                },
                _getPageByUrl: function (url)
                {
                    let
                        page = null;

                    this.$$pageContainer
                        .find(">.rad-control-page")
                        .forEach(item =>
                        {
                            const
                                control = $$.radControl(item);

                            if (control?.urlGet() === url || item.getAttribute("data-url") === url)
                            {
                                page = control;
                                return false;
                            }
                        });

                    return page;
                },
                _getPageElementByUrl: function (url)
                {
                    var
                        page = this._getPageByUrl(url);

                    return page ? page.$$elementGet() : null;
                },
                _getResourceUrl: function (url)
                {
                    var
                        serverPageInfo = {
                            sourceUrl: url,
                            destinationUrl: url
                        };

                    $$(document).triggerHandler("seriouslyrad.ui.PageManager:MapServerPageUrl", serverPageInfo);

                    const
                        destinationLocation = window.pathHelper.getLocation(serverPageInfo.destinationUrl),
                        isEmbeddedResource = destinationLocation.applicationRelativeUrl.startsWith("embeddedresources/"),
                        pathSlug =
                            isEmbeddedResource && window.applicationInformation.cordova
                                ? String.empty
                                : "pages/";

                    return `${window.applicationInformation.virtualDirectory}${pathSlug}${destinationLocation.applicationRelativeUrl}`;
                },
                _historyCleared: function ()
                {
                    this.clearHistoryAsyncPromise.resolve();
                    this.clearHistoryAsyncPromise = null;
                },
                _init: function ()
                {
                    var
                        self = this;

                    $$(document).trigger("seriouslyrad.ui.PageManager:beforeInitialize", self);

                    self._featureDetectScrollbars();
                    self.$$pageContainer = $$(".rad-page-container");

                    if (!self.$$pageContainer)
                    {
                        self.$$pageContainer =
                            $$.create$$({ nodeName: "div", attributes: { "class": "rad-page-container" } })
                                .appendTo(document.body);
                    }

                    this.$$pageContainer
                        .find(">.rad-control-page")
                        .forEach(item =>
                        {
                            item.setAttribute("data-no-cache", true);
                            item.setAttribute("data-title", document.title);
                            item.setAttribute("data-url", "/" + window.pathHelper.getLocation(window.location).applicationRelativeUrl);
                        });
                    
                    if (!self._initializationPromises.length)
                        setTimeout(() => { self._queueInitialNavigation(); });
                    else
                    {
                        self.progressLoader.valueSet(0);
                        self.progressLoader.totalSet(self._initializationPromises.length);
                        self.progressLoader.enqueue();

                        self._initializationPromises
                            .each(item => { item.then(() => { self.progressLoader.valueSet(self.progressLoader.valueGet() + 1); }); });

                        Promise.all(self._initializationPromises)
                            .then(() =>
                            {
                                self.progressLoader.dequeue();
                                setTimeout(() => { self._queueInitialNavigation(); });
                            });
                    }
                },
                _loadPageViewAsync: function (url, promise)
                {
                    const
                        self = this,
                        promiseInternal = promise ?? Promise.public();

                    var
                        onAuthenticated = self.onAuthenticatedGet(),
                        data, xhr, headers = {};

                    self._addAuthenticationHeaders(headers);

                    $$
                        .ajax({
                            headers: headers,
                            type: "GET",
                            url: self._getResourceUrl(url),
                            xhrFields: {
                                withCredentials: true
                            }
                        })
                        .then(results =>
                        {
                            const
                                dataInner = results[0],
                                xhrInner = results[1];

                            data = dataInner;
                            xhr = xhrInner;

                            var
                                authenticated = xhr.getResponseHeader("rad-authenticated");

                            if (onAuthenticated && authenticated?.toLowerCase() === true.toString().toLowerCase())
                                return onAuthenticated();
                        })
                        .then(() =>
                        {
                            var
                                $$data = $$(data),
                                $$page = $$data.hasClass("rad-control-page") ? $$data : $$data.find(".rad-control-page").first();

                            if (!window.cordova)
                            {
                                var
                                    cacheControlProcessResult = self._processCacheControlHeader(xhr.getResponseHeader("cache-control"));

                                $$page
                                    .attr("data-cache-expiry", cacheControlProcessResult.cacheExpiry)
                                    .attr("data-no-cache", cacheControlProcessResult.noCache);
                            }

                            $$page
                                .attr("data-url", url)
                                .hide();

                            if ($$page.length)
                            {
                                $$page.css("display", "none");
                                $$(document).trigger("seriouslyrad.ui.PageManager:pageContentLoaded", $$page);

                                var
                                    page = seriouslyrad.ui.web.Control.createFromElement($$page);

                                if (page && page instanceof seriouslyrad.ui.web.Page)
                                {
                                    promiseInternal.resolve(page);
                                    return;
                                }
                            }

                            $$page.remove();
                            promiseInternal.reject("Loaded URL does not contain a page");
                        })
                        .catch(xhrOrError =>
                        {
                            if (xhrOrError instanceof XMLHttpRequest)
                            {
                                const
                                    xhr = xhrOrError;

                                if (xhr.readyState === 0 || xhr.status <= 0 || xhr.status === 503 || !navigator.onLine)
                                {
                                    if (!self.disconnectedDialog)
                                        self.disconnectedDialog = new seriouslyrad.ui.web.MessageDialog();

                                    if (xhr.status === 503)
                                    {
                                        self.disconnectedDialog.titleSet("Server Unavailable");
                                        self.disconnectedDialog.textSet("The server is currently unavailable.");
                                    }
                                    else
                                    {
                                        self.disconnectedDialog.titleSet("Network Error");
                                        self.disconnectedDialog.textSet("A network connection issue has been detected. Please ensure you have an internet connection and click \"OK\" to try again.");
                                    }

                                    self.disconnectedDialog
                                        .openAsync()
                                        .then(() =>
                                        {
                                            setTimeout(() => { self._loadPageViewAsync(url, promiseInternal) });
                                        });

                                    return;
                                }

                                if (xhr.status !== undefined)
                                {
                                    if (xhr.status === 401)
                                    {
                                        const
                                            onUnauthenticated = self.onUnauthenticatedGet();

                                        if (onUnauthenticated)
                                            onUnauthenticated();

                                        promiseInternal.reject(xhr);

                                    }
                                    else
                                    {
                                        const
                                            error = window.serverErrorHelper.processError(xhr);

                                        seriouslyrad.ui.web.ErrorDialog.displayError(error);
                                        promiseInternal.reject(error);
                                    }

                                    return;
                                }
                            }

                            seriouslyrad.ui.web.ErrorDialog.displayError(xhrOrError);
                            promiseInternal.reject(xhrOrError);
                        });

                    return promiseInternal;
                },
                _navigateAsync: function (navigationQueueItem)
                {
                    var
                        self = this;

                    return new Promise((resolve, reject) =>
                    {
                        var
                            currentPage = self.currentPageGet();

                        if (!currentPage)
                            self.currentPageSet(navigationQueueItem.page);

                        if (currentPage === navigationQueueItem.page)
                            resolve();
                        else
                        {
                            navigationQueueItem.transitionType = (navigationQueueItem.back ? currentPage : navigationQueueItem.page).transitionTypeGet();
                            navigationQueueItem.transitionName = (navigationQueueItem.back ? currentPage : navigationQueueItem.page).transitionNameGet();
                            navigationQueueItem.page.navigationDataSet(navigationQueueItem.navigationData);

                            self._setScrollableForPage(navigationQueueItem.page);

                            $$(document).triggerHandler("seriouslyrad.ui.PageManager:Navigating", { currentPage: currentPage, newPage: navigationQueueItem.page, pageManager: self });

                            self._showPageAsync(navigationQueueItem, currentPage)
                                .then(() =>
                                {
                                    self._processQueryString();
                                    $$(document).triggerHandler("seriouslyrad.ui.PageManager:Navigated", { pageManager: self });
                                    navigationQueueItem.page.triggerHandler("PageManagerAfterShowPage", { self: navigationQueueItem.page });
                                    resolve();
                                })
                                .catch(error =>
                                {
                                    $$(document).triggerHandler("seriouslyrad.ui.PageManager:NavigateFailed", { currentPage: currentPage, pageManager: self });
                                    self._setScrollableForPage(currentPage);
                                    reject(error);
                                });
                        }
                    });
                },
                _navigationBlocked: function (navigationQueueItem)
                {
                    var
                        self = this,
                        promise = Promise.public(),
                        navigationBlocked = self._blockNavigationFunctions.any(item => item(promise) === true);

                    if (navigationBlocked)
                    {
                        // If the promise is ever resolved, the navigation is re-attempted
                        promise
                            .then(() =>
                            {
                                self._queueNavigation(navigationQueueItem);
                            });
                    }

                    return navigationBlocked;
                },
                _processQueryString: function ()
                {
                    var
                        location = window.pathHelper.getLocation(window.location),
                        queryStringParameters = window.pathHelper.getQueryStringParameters(window.location),
                        url = location.pathname;

                    // Add initial trailing slash
                    if (!url.endsWith("/") && !window.cordova)
                        url = url + "/";

                    if (queryStringParameters.length)
                    {
                        queryStringParameters
                            .each(item => { window.applicationInformation.queryStringParameters[item.name] = item.value; });

                        history.replaceState({ counter: history.pushStateCounter, navigationData: null }, null, url);
                    }

                    return url;
                },
                _processCacheControlHeader: function (cacheControlHeader)
                {
                    var
                        maxAgeRegex = /max-age=([\d]*)/,
                        noCacheRegex = /(no-store|no-cache)/,
                        noCache = cacheControlHeader === null ? false : noCacheRegex.test(cacheControlHeader),
                        cacheExpiry = cacheControlHeader === null ? -1 : maxAgeRegex.test(cacheControlHeader) ? maxAgeRegex.exec(cacheControlHeader)[1] : -1;

                    return { noCache: noCache, cacheExpiry: cacheExpiry };
                },
                _processNavigationQueue: function ()
                {
                    var
                        self = this;

                    if (self.navigationSuspendedGet())
                        return;

                    if (!self._navigationQueue.length)
                        return;

                    if (self._processingNavigationQueue)
                        return;

                    self._processingNavigationQueue = true;

                    var
                        navigationQueueItem;

                    // Skip intermediate navigations
                    while (self._navigationQueue.length)
                    {
                        navigationQueueItem = self._navigationQueue.shift();

                        if (self._navigationQueue.length && navigationQueueItem.promise)
                            navigationQueueItem.promise.resolve();
                    }

                    self._processNavigationQueueItem(navigationQueueItem)
                        .then(() =>
                        {
                            if (navigationQueueItem.replaceUrl)
                                self.replaceUrl(navigationQueueItem.url);
                        })
                        .finally(() =>
                        {
                            self._processingNavigationQueue = false;
                            self._processNavigationQueue();
                        });
                },
                _processNavigationQueueItem: function (navigationQueueItem)
                {
                    var
                        self = this;

                    self.currentNavigationQueueItem = navigationQueueItem;

                    return new Promise((resolve, reject) =>
                    {
                        var
                            currentPage = self.currentPageGet();

                        if (currentPage?.urlGet() === navigationQueueItem.url)
                        {
                            self.currentNavigationQueueItem = null;
                            resolve();
                            return;
                        }

                        navigationQueueItem.$$page = self._getPageElementByUrl(navigationQueueItem.url);

                        if (!navigationQueueItem.$$page)
                        {
                            self
                                ._loadPageViewAsync(navigationQueueItem.url)
                                .then(page =>
                                {
                                    navigationQueueItem.page = page;
                                    navigationQueueItem.$$page = page.$$elementGet();

                                    if (navigationQueueItem.onPageReady)
                                        navigationQueueItem.onPageReady(navigationQueueItem.page);

                                    return self._navigateAsync(navigationQueueItem);
                                })
                                .then(() =>
                                {
                                    self.currentNavigationQueueItem = null;
                                    resolve();

                                    if (navigationQueueItem.promise)
                                        navigationQueueItem.promise.resolve();
                                })
                                .catch(error =>
                                {
                                    self.currentNavigationQueueItem = null;
                                    reject(error);

                                    if (navigationQueueItem.promise)
                                        navigationQueueItem.promise.reject(error);
                                });
                        }
                        else
                        {
                            var
                                pageControl = navigationQueueItem.$$page.radControl();

                            navigationQueueItem.$$page.css("display", "none");
                            navigationQueueItem.page =
                                pageControl
                                    ? pageControl
                                    : seriouslyrad.ui.web.Control.createFromElement(navigationQueueItem.$$page);

                            if (navigationQueueItem.onPageReady)
                                navigationQueueItem.onPageReady(navigationQueueItem.page);

                            self._navigateAsync(navigationQueueItem)
                                .then(() =>
                                {
                                    self.currentNavigationQueueItem = null;
                                    resolve();

                                    if (navigationQueueItem.promise)
                                        navigationQueueItem.promise.resolve();
                                })
                                .catch(error =>
                                {
                                    self.currentNavigationQueueItem = null;
                                    reject(error);

                                    if (navigationQueueItem.promise)
                                        navigationQueueItem.promise.reject(error);
                                });
                        }
                    });
                },
                _queueNavigation: function (navigationQueueItem)
                {
                    if (this._navigationBlocked(navigationQueueItem))
                        return;

                    this._navigationQueue.push(navigationQueueItem);
                    this._processNavigationQueue();
                },
                _queueInitialNavigation: function ()
                {
                    this._setPushStateCounter(history.state?.counter || 0);

                    window.applicationInformation.queryStringParameters = {};

                    var
                        url = this._processQueryString();

                    this._queueNavigation({
                        initialNavigation: true,
                        back: false,
                        historyAction: namespace.PageManager.historyActions.none,
                        navigationData: null,
                        url: url,
                        title: document.title,
                        replaceUrl: !!window.location.hash
                    });
                },
                _setPushStateCounter: function (value)
                {
                    history.pushStateCounter = value;

                    if (history.pushStateCounter === 0)
                        $$("html").addClass("rad-state-back-hidden");
                    else
                        $$("html").removeClass("rad-state-back-hidden");
                },
                _setScrollableForPage: function (page)
                {
                    if (page?.scrollableGet())
                        this.$$pageContainer.css({ "overflow-y": "hidden" });
                    else
                        this.$$pageContainer.css({ "overflow-y": String.empty });
                },
                _showPageAsync: function (navigationQueueItem, currentPage)
                {
                    var
                        self = this;

                    return new Promise((resolve, reject) =>
                    {
                        var
                            pageLoadAndOrActivatePromise;

                        self._backupTabIndexesIfPageDimmed(navigationQueueItem.$$page);

                        if (!currentPage)
                        {
                            navigationQueueItem.$$page.appendTo(self.$$pageContainer);
                            navigationQueueItem.$$page.css("display", String.empty);
                            navigationQueueItem.page.show();

                            if (!navigationQueueItem.page[".loaded"])
                            {
                                pageLoadAndOrActivatePromise =
                                    navigationQueueItem.page.loadAsync()
                                        .then(() =>
                                        {
                                            navigationQueueItem.page[".loaded"] = true;
                                            return navigationQueueItem.page.activateAsync();
                                        });
                            }
                            else
                                pageLoadAndOrActivatePromise = navigationQueueItem.page.activateAsync();

                            pageLoadAndOrActivatePromise
                                .then(() =>
                                {
                                    self._updateCurrentPageFromNavigationQueueItem(navigationQueueItem);
                                    self._removeOldPages();
                                    resolve();
                                })
                                .catch(error =>
                                {
                                    reject(error);
                                });
                        }
                        else
                        {
                            self.pageTransitionerGet().executeAsync(self, navigationQueueItem)
                                .then(() =>
                                {
                                    return currentPage.deactivateAsync();
                                })
                                .then(() =>
                                {
                                    self._updateCurrentPageFromNavigationQueueItem(navigationQueueItem);
                                    self._removeOldPages();
                                    resolve();
                                })
                                .catch(error =>
                                {
                                    reject(error);
                                });
                        }
                    });
                },
                _updateCurrentPageFromNavigationQueueItem: function (navigationQueueItem)
                {
                    if (history.pushStateCounter === undefined)
                        this._setPushStateCounter(history.state ? history.state.counter : 0);

                    switch (navigationQueueItem.historyAction)
                    {
                        case namespace.PageManager.historyActions.push:
                            this._setPushStateCounter(history.pushStateCounter + 1);
                            history.pushState({ counter: history.pushStateCounter, navigationData: navigationQueueItem.navigationData }, null, navigationQueueItem.page.urlGet());
                            break;
                        case namespace.PageManager.historyActions.replace:
                            history.replaceState({ counter: history.pushStateCounter, navigationData: navigationQueueItem.navigationData }, null, navigationQueueItem.page.urlGet());
                            break;
                    }

                    this._updateDocumentTitle(navigationQueueItem.page.titleGet());

                    this._currentPageSet(navigationQueueItem.page);
                },
                _updateDocumentTitle: function (title)
                {
                    var
                        stringTitle = title ? title : String.empty;

                    $$("title").html(stringTitle.htmlEncode());
                    document.title = stringTitle;
                },

                // Public Methods
                clear: function (keepCurrentPage)
                {
                    var
                        currentPage = this.currentPageGet();

                    this.$$pageContainer
                        .find(">.rad-control-page")
                        .forEach(item =>
                        {
                            var
                                page = $$.radControl(item);

                            if (!page)
                                return;

                            if (!keepCurrentPage || page !== currentPage)
                                page.remove();
                        });

                    if (!keepCurrentPage)
                        this.currentPageSet(null);
                },
                clearHistoryAsync: function ()
                {
                    var
                        self = this;

                    if (self.clearHistoryAsyncPromise)
                        return self.clearHistoryAsyncPromise;

                    self.clearHistoryAsyncPromise = Promise.public();

                    if (history.pushStateCounter > 0)
                    {
                        self._clearingHistory = true;
                        window.history.go(history.pushStateCounter * -1);
                    }
                    else
                    {
                        setTimeout(() =>
                        {
                            self.clearHistoryAsyncPromise.resolve();
                            self.clearHistoryAsyncPromise = null;
                        }, 100);
                    }

                    return (
                        self.clearHistoryAsyncPromise
                            .then(() =>
                            {
                                self._navigationQueue = [];
                                self._processingNavigationQueue = false;
                            })
                    );
                },
                clearPagesOpenedAsDialogs: function ()
                {
                    delete this._pagesOpenedAsDialog;
                },
                openPageAsDialogAsync: function (url, navigationData, useCache)
                {
                    if (!this._pagesOpenedAsDialog)
                        this._pagesOpenedAsDialog = {};

                    const
                        resourceUrl = this._getResourceUrl(url),
                        loadPagePromise =
                            useCache && this._pagesOpenedAsDialog[resourceUrl]
                                ? Promise.resolve(this._pagesOpenedAsDialog[resourceUrl])
                                : this._loadPageViewAsync(url)
                                    .then(page => { this._pagesOpenedAsDialog[resourceUrl] = page; return page; });

                    return (
                        loadPagePromise
                            .then(page =>
                            {
                                page.navigationDataSet(navigationData);
                                page.$$elementGet().css("display", String.empty);
                                return page.openAsDialogAsync();
                            })
                    );
                },
                navigateAsync: function (url, replace, back, navigationData, onPageReady)
                {
                    const
                        promise = Promise.public(),
                        navigation = {
                            back: !!back,
                            promise: promise,
                            historyAction: replace ? namespace.PageManager.historyActions.replace : namespace.PageManager.historyActions.push,
                            navigationData: navigationData || null,
                            onPageReady: onPageReady,
                            url: url
                        };

                    let
                        handled = false;

                    for (let i = 0; i < this._navigationHandlers.length; i++)
                    {
                        if (this._navigationHandlers[i](navigation))
                        {
                            handled = true;
                            break;
                        }
                    }

                    if (!handled)
                        this._queueNavigation(navigation);

                    return promise;
                },
                registerBlockNavigationFunction: function (fn)
                {
                    if (!this._blockNavigationFunctions.contains(fn))
                        this._blockNavigationFunctions.push(fn);
                },
                registerInitializationPromise: function (promise)
                {
                    this._initializationPromises.push(promise);
                },
                registerNavigationHandler: function (fn)
                {
                    this._navigationHandlers.push(fn);
                },
                reloadCurrentPageAsync: function ()
                {
                    const
                        location = window.pathHelper.getLocation(window.location);

                    if (this._processingNavigationQueue && !this.currentNavigationQueueItem?.initialNavigation)
                    {
                        this._navigationQueue = [];
                        this._processingNavigationQueue = false;
                    }

                    return this.navigateAsync(location.pathname + location.search + location.hash, true);
                },
                replaceUrl: function (url)
                {
                    history.replaceState(history.state, null, url);
                },
                resumeNavigation: function ()
                {
                    if (!this.navigationSuspendedGet())
                        return;

                    this.navigationSuspendedSet(false);
                    this._processNavigationQueue();
                },
                suspendNavigation: function ()
                {
                    this.navigationSuspendedSet(true);
                },
                unRegisterBlockNavigationFunction: function (fn)
                {
                    this._blockNavigationFunctions =
                        this._blockNavigationFunctions.where(item => item !== fn);
                },
                unRegisterNavigationHandler: function (fn)
                {
                    this._navigationHandlers =
                        this._navigationHandlers.where(item => item !== fn);
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            classConstructor.historyActions = {
                none: "none",
                push: "push",
                replace: "replace"
            };

            $$(() =>
            {
                $$(document).on("click", "a", evt =>
                {
                    if (!window.pageManager)
                        return;

                    const
                        target = evt.currentTarget.getAttribute("target"),
                        href = evt.currentTarget.getAttribute("href");

                    if (target && target !== "_self")
                        return;

                    if (!href || href === "#")
                        return;

                    // for #'s, if an anchor exists with this href as it's name, bail out...
                    if (isHashTargettingAnchorOnPage(href))
                        return;

                    var
                        newLocation = pathHelper.getLocation(href);

                    if (window.location.origin !== newLocation.origin)
                        return;

                    if (newLocation.pathname + newLocation.search !== location.pathname + location.search)
                        window.pageManager.navigateAsync(newLocation.pathname + newLocation.search + newLocation.hash);

                    evt.preventDefault();
                });

                var
                    cancellingNavigation = false;

                $$(window).on("popstate", evt =>
                {
                    if (!window.pageManager)
                        return;

                    // for #'s, if an anchor exists with this href as it's name, bail out...
                    if (isHashTargettingAnchorOnPage(window.location.hash))
                        return;

                    var
                        state = evt.originalEvent.state || { navigationData: null },
                        pageCounter = state.counter || 0,
                        back = pageCounter < history.pushStateCounter;

                    window.pageManager._setPushStateCounter(pageCounter);

                    if (window.pageManager._clearingHistory)
                    {
                        window.pageManager._clearingHistory = false;
                        window.pageManager._historyCleared();
                        return;
                    }

                    if (cancellingNavigation)
                    {
                        cancellingNavigation = false;
                        return;
                    }

                    var
                        location = window.pathHelper.getLocation(window.location),
                        navigationQueueItem = {
                            back: back,
                            historyAction: namespace.PageManager.historyActions.none,
                            navigationData: state.navigationData || null,
                            url: location.pathname + location.search + location.hash
                        };

                    if (window.pageManager._navigationBlocked(navigationQueueItem))
                    {
                        cancellingNavigation = true;

                        if (back)
                            window.history.forward();
                        else
                            window.history.back();

                        return;
                    }

                    window.pageManager._queueNavigation(navigationQueueItem);
                });

                function isHashTargettingAnchorOnPage (hash)
                {
                    return hash.startsWith("#") && $$("a[name='" + hash.replace("#", "") + "']").length > 0;
                }
            });
        }
    });

    // #endregion

    // #region Drag & Drop Manager

    classFactory.create({
        createConstructor: namespace =>
        {
            return function ()
            {
                const
                    DRAGGABLE_BORDER_WIDTH = 1,
                    self = this;

                self.inputTypeEnum =
                {
                    mouse: 0,
                    touch: 1
                };

                self.inputType = null;

                let
                    $$item = null,
                    control = null,
                    $$area = null,
                    $$draggable = null,
                    $$debugPanel = null,
                    $$document = $$(document),
                    $$page = null,
                    $$pageElementToScroll = null,
                    $$elementForSizing = null,
                    
                    itemAllowedParentControlClasses = null,

                    initialTargetOffset = null,
                    initialMouseOffset = null,

                    scrollBoundaryActivations = null,
                    scrollTimerHandle = null,
                    scrollTimerInterval = 50,
                    scrollAmount = 30,
                    scrollDuration = 10,
                    scrollVerticalInProgress = false,
                    scrollHorizontalInProgress = false,
                    lastMovementCoords = null,
                    pageOffset = null,

                    itemOptions;

                if (ENABLE_DRAG_DEBUG)
                {
                    $$debugPanel =
                        $$.create$$({ nodeName: "div", attributes: { "style": "background-color: white; width: 400px; height:350px; position: fixed; top:150px; right:50px; z-index: 9999; border:1px solid;" } })
                            .appendTo(document.body);
                }

                $$(document).on("click", ".rad-drag-drop-handle", evt =>
                {
                    evt.preventDefault();
                    evt.stopPropagation();
                });

                function shouldStart($$target)
                {
                    var
                        $$dragdropItem = $$target.closest(".rad-drag-drop-item"),
                        $$dragDropArea = $$dragdropItem.parent();

                    if ($$dragdropItem.length === 0 || $$dragdropItem.hasClass("rad-drag-drop-item-disabled"))
                        return false;

                    if (!$$dragDropArea || !$$dragDropArea.hasClass("rad-drag-drop-area"))
                    {
                        console.error("Drag drop item is not a direct child of a drag drop area");
                        return false;
                    }

                    return true;
                }

                function inputStart($$target, coords)
                {
                    $$item = $$target.closest(".rad-drag-drop-item");
                    itemOptions = $$item.data("dragDropManager:options");
                    control = $$item.radControl();
                    $$area = $$item.parent();

                    var
                        itemOffset = $$item.offset(),
                        getDraggableContainer = $$item.data("getDraggableContainer");

                    if (getDraggableContainer)
                    {
                        $$page = getDraggableContainer();
                        $$pageElementToScroll = $$page.first();
                    }
                    else
                    {
                        $$page = $$area.closest(".rad-control-page");
                        $$pageElementToScroll = $$page.find(".rad-control-container-wrapper").first();
                    }

                    pageOffset = $$page.offset();
                    $$elementForSizing = $$page;

                    initialTargetOffset = { top: itemOffset.top - pageOffset.top, left: itemOffset.left - pageOffset.left + $$pageElementToScroll.scrollLeft() };
                    initialMouseOffset = { top: coords.y + DRAGGABLE_BORDER_WIDTH, left: coords.x + DRAGGABLE_BORDER_WIDTH };

                    itemAllowedParentControlClasses = getClassDraggableDataFromElement($$item).allowedParentControlClasses;
                }

                function doBoundaryScrollOnInterval()
                {
                    if (itemOptions?.inhibitScroll)
                        return;

                    if (scrollBoundaryActivations !== null)
                    {
                        var
                            scrollAdjustmentAmount = scrollBoundaryActivations.top || scrollBoundaryActivations.left ? 1 - scrollAmount : scrollAmount;

                        if (scrollBoundaryActivations.top || scrollBoundaryActivations.bottom)
                        {
                            if (!scrollVerticalInProgress)
                            {
                                if (
                                    scrollBoundaryActivations.top && $$pageElementToScroll.scrollTop() + scrollAdjustmentAmount >= 0
                                    || scrollBoundaryActivations.bottom && $$pageElementToScroll.scrollTop() + scrollAdjustmentAmount <= $$document.height()
                                   )
                                {
                                    scrollVerticalInProgress = true;

                                    var
                                        lastScrollTop = $$pageElementToScroll.scrollTop();

                                    $$pageElementToScroll
                                        .animate(
                                            {
                                                scrollTop: $$pageElementToScroll.scrollTop() + scrollAdjustmentAmount
                                            },
                                            scrollDuration,
                                            "linear"
                                        )
                                        .promise()
                                        .finally(() =>
                                        {
                                            scrollVerticalInProgress = false;
                                            if (lastScrollTop !== $$pageElementToScroll.scrollTop())
                                                inputMove(null, { x: lastMovementCoords.x, y: lastMovementCoords.y });
                                        });
                                }
                            }
                        }

                        if (scrollBoundaryActivations.left || scrollBoundaryActivations.right)
                        {
                            if (!scrollHorizontalInProgress)
                            {
                                if (
                                    scrollBoundaryActivations.left && $$pageElementToScroll.scrollLeft() + scrollAdjustmentAmount >= 0
                                    || scrollBoundaryActivations.right && $$pageElementToScroll.width() + $$pageElementToScroll.scrollLeft() + scrollAdjustmentAmount <= $$pageElementToScroll.width()
                                   )
                                {
                                    scrollHorizontalInProgress = true;

                                    var
                                        lastScrollLeft = $$pageElementToScroll.scrollLeft();

                                    $$pageElementToScroll
                                        .animate(
                                            {
                                                scrollLeft: $$pageElementToScroll.scrollLeft() + scrollAdjustmentAmount
                                            },
                                            scrollDuration,
                                            "linear"
                                        )
                                        .promise()
                                        .finally(() =>
                                        {
                                            scrollHorizontalInProgress = false;
                                            if (lastScrollLeft !== $$pageElementToScroll.scrollLeft())
                                                inputMove(null, { x: lastMovementCoords.x, y: lastMovementCoords.y });
                                        });
                                }
                            }
                        }
                    }
                }

                function inputMove($$target, coords)
                {
                    if ($$item === null || $$area === null)
                        return;

                    if (!$$draggable)
                    {
                        $$draggable = $$.create$$({ nodeName: "div", attributes: { "class": "rad-drag-drop-draggable" } });

                        var
                            $$clone = $$($$item[0].outerHTML);

                        self.disableDragDrops($$clone);
                        $$clone.css({ marginTop: 0 });
                        $$draggable.css({
                            position: "absolute",
                            "z-index": 9999,
                            width: $$item.outerWidth() + 2 * DRAGGABLE_BORDER_WIDTH,
                            height: $$item.outerHeight() + 2 * DRAGGABLE_BORDER_WIDTH
                        });
                        $$draggable.append($$clone);

                        var
                            onDraggableCreated = $$item.data("onDraggableCreated");

                        if (onDraggableCreated)
                            $$draggable = onDraggableCreated($$draggable, $$clone);

                        $$draggable.appendTo($$page);

                        $$item.css({ opacity: 0.3 });

                        if (control)
                            $$draggable.addClass(control[".classDefinition"].classConstructor.dragDropDraggableCssClass);

                        $$draggable.css(initialTargetOffset);

                        $$("html").addClass("rad-drag-drop-active rad-drag-drop-overflow-flag");
                        $$item.radControl()?.trigger("dragDropStarted", { $$area: $$area, $$draggable: $$draggable });
                        self.triggerHandler("dragDropStarted", { $$item: $$item, $$area: $$area, $$draggable: $$draggable });

                        scrollTimerHandle = setInterval(() => { doBoundaryScrollOnInterval(); }, scrollTimerInterval);
                        setTimeout(() => { $$("html").removeClass("rad-drag-drop-overflow-flag"); }, 100);
                    }

                    var
                        newPosition = {
                        };

                    if (!itemOptions || !itemOptions.inhibitHorizontal)
                        newPosition.left = initialTargetOffset.left - (initialMouseOffset.left - coords.x);

                    if (!itemOptions || !itemOptions.inhibitVertical)
                        newPosition.top = initialTargetOffset.top - (initialMouseOffset.top - coords.y);

                    $$draggable.css(newPosition);

                    var
                        pageElementScrollPosition = { top: $$pageElementToScroll.scrollTop(), left: $$pageElementToScroll.scrollLeft() },
                        scollBoundaryAreaSize = { width: $$elementForSizing.width(), height: $$elementForSizing.height() },
                        boundaryActivationDistancePercentage = 10,
                        calculatedBoundaryActivationDistances = { width: scollBoundaryAreaSize.width / 100 * boundaryActivationDistancePercentage, height: scollBoundaryAreaSize.height / 100 * boundaryActivationDistancePercentage };

                    lastMovementCoords = { x: coords.x, y: coords.y };

                    scrollBoundaryActivations = {
                        top: coords.y - pageOffset.top <= calculatedBoundaryActivationDistances.height,
                        left: coords.x - pageOffset.left <= calculatedBoundaryActivationDistances.width,
                        bottom: coords.y - pageOffset.top >= scollBoundaryAreaSize.height - calculatedBoundaryActivationDistances.height,
                        right: coords.x - pageOffset.left >= scollBoundaryAreaSize.width - calculatedBoundaryActivationDistances.width
                    };

                    if (ENABLE_DRAG_DEBUG)
                    {
                        $$debugPanel.html("<span><u><b>Drag Debug</b></u></span><br><span>coords x/y: " + coords.x + ":" + coords.y + "</span>");
                        $$debugPanel.append("<br><span>initial target offset: " + initialTargetOffset.toJson() + "</span>");
                        $$debugPanel.append("<br><span>initial mouse offset: " + initialMouseOffset.toJson() + "</span>");
                        $$debugPanel.append("<br><span>pageElementScrollPosition: " + pageElementScrollPosition.toJson() + "</span>");
                        $$debugPanel.append("<br/><span>calculatedBoundaryActivationDistances: " + calculatedBoundaryActivationDistances.toJson() + "</span>");
                        $$debugPanel.append("<br/><span>scrollBoundaryActivations: </span>");
                        $$debugPanel.append("<br/><span>- " + (scrollBoundaryActivations.top ? "<b><u>top</u></b>" : "top") + " (" + (coords.y - pageOffset.top) + " - " + pageElementScrollPosition.top + " <= " + calculatedBoundaryActivationDistances.height + ")</span>");
                        $$debugPanel.append("<br/><span>- " + (scrollBoundaryActivations.left ? "<b><u>left</u></b>" : "left") + "</span>");
                        $$debugPanel.append("<br/><span>- " + (scrollBoundaryActivations.bottom ? "<b><u>bottom</u></b>" : "bottom") + " (" + (coords.y - pageOffset.top) + " >= " + pageElementScrollPosition.top + " + " + scollBoundaryAreaSize.height + " - " + calculatedBoundaryActivationDistances.height + ")</span>");
                        $$debugPanel.append("<br/><span>- " + (scrollBoundaryActivations.right ? "<b><u>right</u></b>" : "right") + "</span>");
                    }
                }

                function inputEnd()
                {
                    if ($$item === null || $$area === null || $$draggable === null)
                        return;

                    $$item.css({ opacity: String.empty });
                    $$draggable.remove();
                    $$draggable.empty();

                    $$("html").removeClass("rad-drag-drop-active");
                    $$item.radControl()?.trigger("dragDropEnded", { $$area: $$area });
                    self.triggerHandler("dragDropEnded", { $$item: $$item, $$area: $$area });

                    clearInterval(scrollTimerHandle);
                    scrollBoundaryActivations = null;
                    scrollHorizontalInProgress = false;
                    scrollVerticalInProgress = false;
                    $$item = null;
                    control = null;
                    $$area = null;
                    $$draggable = null;
                    $$page = null;
                    pageOffset = null;
                    //$$pageElementToScroll = null;
                    $$elementForSizing = null;
                    itemAllowedParentControlClasses = null;

                    $$(document).off("mousemove touchmove", self.onInputMove);

                    $$(document).off("mousemove", self.onMouseMove);
                    $$(document).off("mouseup", self.onMouseUp);

                    $$(document).off("touchmove", self.onTouchMove);
                    $$(document).off("touchend", self.onTouchEnd);
                }

                function getCoords(inputType, evt)
                {
                    switch (inputType)
                    {
                        case self.inputTypeEnum.mouse:
                            {
                                return { x: evt.pageX, y: evt.pageY };
                            }
                        case self.inputTypeEnum.touch:
                            {
                                var input = evt.originalEvent.touches[0] || evt.originalEvent.changedTouches[0];
                                return { x: input.pageX, y: input.pageY };
                            }

                    }
                    return null;
                }

                function processDragDropAreaChange(inputType, evt, target)
                {
                    var
                        $$newArea = $$(target),
                        areaOptions = $$newArea.data("dragDropManager:options");

                    if ($$item[0] === target || $$.contains($$item[0], target) || $$newArea.hasClass("rad-drag-drop-area-disabled"))
                        return;

                    if ($$newArea.hasClass("rad-drag-drop-area-single-item") && $$newArea.find(">.rad-drag-drop-item").length !== 0)
                        return;

                    if (itemOptions?.requiredCssClasses)
                    {
                        if (!$$newArea.hasAnyClass(itemOptions.requiredCssClasses))
                            return;
                    }

                    if (areaOptions?.acceptedCssClasses)
                    {
                        if (!$$item.hasAnyClass(areaOptions.acceptedCssClasses))
                            return;
                    }

                    var
                        $$insertBefore = null,
                        coords = getCoords(inputType, evt);

                    $$newArea.find(">.rad-drag-drop-item")
                        .each((index, element) =>
                        {
                            const
                                $$element = $$(element),
                                offset = $$element.offset();

                            if (offset.top + $$element.outerHeight(true) / 2 > coords.y)
                            {
                                $$insertBefore = $$element;
                                return false;
                            }
                        });

                    if ($$insertBefore)
                        $$item.insertBefore($$insertBefore);
                    else
                        $$item.appendTo($$newArea);

                    if (!$$area.is($$newArea))
                    {
                        $$item.radControl()?.trigger("dragDropAreaChanged", { $$area: $$newArea, $$areaOriginal: $$area });
                        self.triggerHandler("dragDropAreaChanged", { $$item: $$item, $$area: $$newArea, $$areaOriginal: $$area });
                    }

                    $$area = $$newArea;
                }

                function isMouseOverPadding($$target, coords)
                {
                    if (!$$target || !coords)
                        return false;

                    var
                        padding = {
                            left: parseInt($$target.css("padding-left")),
                            right: parseInt($$target.css("padding-right")),
                            top: parseInt($$target.css("padding-top")),
                            bottom: parseInt($$target.css("padding-bottom"))
                        };

                    if (padding.left <= 0 && padding.right <= 0 && padding.top <= 0 && padding.bottom <= 0)
                        return false;

                    var
                        offset = $$target.offset(),
                        size = {
                            width: $$target.outerWidth(),
                            height: $$target.outerHeight()
                        };

                    if (padding.top > 0 && coords.y < offset.top + padding.top)
                        return true;
                    else if (padding.left > 0 && coords.x < offset.left + padding.left)
                        return true;
                    else if (padding.right > 0 && coords.x > offset.left + size.width - padding.right)
                        return true;
                    else if (padding.bottom > 0 && coords.y > offset.top + size.height - padding.bottom)
                        return true;

                    return false;
                }

                function getClassDraggableDataFromElement($$target)
                {
                    var
                        results = {
                            allowedChildControlClasses: null,
                            allowedParentControlClasses: null,
                            targetClassFullName: ""
                        };

                    if (!$$target)
                        return results;

                    var
                        $$targetControlElement = $$target.closest(".rad-control");

                    if (!$$targetControlElement)
                        return results;

                    var
                        targetControl = $$targetControlElement.radControl();

                    results.allowedChildControlClasses = targetControl[".classDefinition"].classConstructor.allowedChildControlClasses;
                    results.allowedParentControlClasses = targetControl[".classDefinition"].classConstructor.allowedParentControlClasses;
                    results.targetClassFullName = targetControl[".classDefinition"].classFullName;

                    return results;
                }

                function isDragItemAllowedInCurrentDragTarget($$target)
                {
                    if ($$item === null || $$target === null)
                        return false;

                    var
                        targetAllowedClassesData = getClassDraggableDataFromElement($$target),
                        itemClassFullName = $$item.radControl()[".classDefinition"].classFullName,
                        itemAllows = true,
                        targetAllows = true;

                    if (targetAllowedClassesData.allowedChildControlClasses?.length)
                        targetAllows = targetAllowedClassesData.allowedChildControlClasses.contains(itemClassFullName);

                    if (itemAllowedParentControlClasses?.length)
                        itemAllows = itemAllowedParentControlClasses.contains(targetAllowedClassesData.targetClassFullName);

                    return itemAllows && targetAllows;
                }

                self.handleMouseDown = evt =>
                {
                    var $$target = $$(evt.currentTarget);
                    if (self.inputType === null && shouldStart($$target))
                    {
                        self.inputType = self.inputTypeEnum.mouse;
                        $$(document).on("mousemove touchmove", { self: self }, self.onInputMove);
                        $$(document).on("mousemove", { self: self }, self.onMouseMove);
                        $$(document).on("mouseup", { self: self }, self.onMouseUp);
                        inputStart($$target, getCoords(self.inputType, evt));
                    }
                };

                self.handleTouchStart = evt =>
                {
                    var $$target = $$(evt.currentTarget);
                    if (self.inputType === null && shouldStart($$target))
                    {
                        self.inputType = self.inputTypeEnum.touch;
                        $$(document).on("mousemove touchmove", { self: self }, self.onInputMove);
                        $$(document).on("touchmove", { self: self }, self.onTouchMove);
                        $$(document).on("touchend", { self: self }, self.onTouchEnd);
                        inputStart($$target, getCoords(self.inputType, evt));
                    }
                };

                self.ENABLE_DRAG_DEBUG = ENABLE_DRAG_DEBUG;
                self.$$debugPanel = $$debugPanel;
                self.getCoords = getCoords;
                self.inputEnd = inputEnd;
                self.inputMove = inputMove;
                self.isDragItemAllowedInCurrentDragTarget = isDragItemAllowedInCurrentDragTarget;
                self.isMouseOverPadding = isMouseOverPadding;
                self.processDragDropAreaChange = processDragDropAreaChange;
            };
        },
        createPrototype: namespace =>
        {
            return {
                // Public Methods
                createDragDropAreas: function ($$elements, options)
                {
                    $$elements.addClass("rad-drag-drop-area");
                    $$elements.forEach(item => { $$.data(item, "dragDropManager:options", options); });

                    if (options)
                    {
                        if (options.cssClass)
                            $$elements.addClass(options.cssClass);

                        if (options.singleItem)
                            $$elements.addClass("rad-drag-drop-area-single-item");
                    }
                },
                createDragDropHandles: function ($$elements, options)
                {
                    $$elements.addClass("rad-drag-drop-handle");
                    $$elements.on("mousedown", this.handleMouseDown);
                    $$elements.on("touchstart", this.handleTouchStart);
                    $$elements.forEach(item => { $$.data(item, "dragDropManager:options", options); });
                },
                createDragDropItems: function ($$elements, options)
                {
                    $$elements.addClass("rad-drag-drop-item");
                    $$elements.forEach(item => { $$.data(item, "dragDropManager:options", options); });

                    if (options)
                    {
                        if (options.onDraggableCreated)
                            $$elements.forEach(item => { $$.data(item, "onDraggableCreated", options.onDraggableCreated); });
                    }
                },

                destroyDragDrops: function ($$elements)
                {
                    $$elements.find("[id]").addBack("[id]").removeAttr("id");
                    this.destroyDragDropAreas($$elements.find(".rad-drag-drop-area").addBack(".rad-drag-drop-area"));
                    this.destroyDragDropItems($$elements.find(".rad-drag-drop-item").addBack(".rad-drag-drop-item"));
                    this.destroyDragDropHandles($$elements.find(".rad-drag-drop-handle").addBack(".rad-drag-drop-handle"));
                },

                destroyDragDropAreas: function ($$elements)
                {
                    var
                        options = $$elements.data("dragDropManager:options");

                    $$elements.removeClass("rad-drag-drop-area");

                    if (options)
                    {
                        if (options.cssClass)
                            $$elements.removeClass(options.cssClass);

                        if (options.singleItem)
                            $$elements.removeClass("rad-drag-drop-area-single-item");
                    }

                    $$elements.removeData("dragDropManager:options");
                },
                destroyDragDropHandles: function ($$elements)
                {
                    $$elements.removeClass("rad-drag-drop-handle");
                    $$elements.off("mousedown", this.handleMouseDown);
                    $$elements.off("touchstart", this.handleTouchStart);
                    $$elements.removeData("dragDropManager:options");
                },
                destroyDragDropItems: function ($$elements)
                {
                    $$elements.removeClass("rad-drag-drop-item");
                    $$elements.removeData("dragDropManager:options");
                },

                disableDragDrops: function ($$elements)
                {
                    $$elements.find("[id]").addBack("[id]").removeAttr("id");
                    this.disableDragDropAreas($$elements.find(".rad-drag-drop-area").addBack(".rad-drag-drop-area"));
                    this.disableDragDropItems($$elements.find(".rad-drag-drop-item").addBack(".rad-drag-drop-item"));
                    this.disableDragDropHandles($$elements.find(".rad-drag-drop-handle").addBack(".rad-drag-drop-handle"));
                },

                disableDragDropAreas: function ($$elements)
                {
                    $$elements.addClass("rad-drag-drop-area-disabled");
                },
                disableDragDropHandles: function ($$elements)
                {
                    $$elements.addClass("rad-drag-drop-handle-disabled");
                    $$elements.off("mousedown", this.handleMouseDown);
                    $$elements.off("touchstart", this.handleTouchStart);
                },
                disableDragDropItems: function ($$elements)
                {
                    $$elements.addClass("rad-drag-drop-item-disabled");
                },

                enableDragDropAreas: function ($$elements)
                {
                    $$elements.removeClass("rad-drag-drop-area-disabled");
                },
                enableDragDropHandles: function ($$elements)
                {
                    $$elements.removeClass("rad-drag-drop-handle-disabled");
                    $$elements.on("mousedown", this.handleMouseDown);
                    $$elements.on("touchstart", this.handleTouchStart);
                },
                enableDragDropItems: function ($$elements)
                {
                    $$elements.removeClass("rad-drag-drop-item-disabled");
                },

                // Event Handlers
                onInputMove: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.inputType !== self.inputTypeEnum.mouse && self.inputType !== self.inputTypeEnum.touch)
                        return;

                    evt.preventDefault();

                    var
                        coords = self.getCoords(self.inputType, evt);

                    coords.x = coords.x - $$(document).scrollLeft();
                    coords.y = coords.y - $$(document).scrollTop();

                    var
                        $$target = $$(document.elementFromPoint(coords.x, coords.y)),
                        deeper = true;

                    // put these back on for isMouseOverPadding
                    coords.x = coords.x + $$(document).scrollLeft();
                    coords.y = coords.y + $$(document).scrollTop();

                    while (deeper)
                    {
                        deeper = false;

                        if (!$$target.hasClass("rad-drag-drop-area"))
                            deeper = true;
                        else if (self.isMouseOverPadding($$target, coords))
                            deeper = true;

                        if ($$target.hasClass("rad-drag-drop-container"))
                            deeper = false;

                        if (deeper)
                            $$target = $$target.parent();

                        if (!$$target.length)
                            return;
                    }

                    if (self.ENABLE_DRAG_DEBUG)
                    {
                        var
                            targetcontrol1 = $$target.closest(".rad-control").radControl(),
                            targetcontrolId = $$target.closest(".rad-control").attr("id");

                        self.$$debugPanel.append("<br><span>targetting: " + targetcontrol1[".classDefinition"].className + " - " + targetcontrolId + ", isDragItemAllowedInCurrentDragTarget?(" + self.isDragItemAllowedInCurrentDragTarget($$target) + ")</span>");
                    }

                    if (!self.isDragItemAllowedInCurrentDragTarget($$target))
                        return;

                    evt.stopPropagation();

                    self.processDragDropAreaChange(self.inputType, evt, $$target[0]);
                },

                onMouseMove: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.inputType === self.inputTypeEnum.mouse)
                        self.inputMove($$(evt.currentTarget), self.getCoords(self.inputType, evt));
                },
                onMouseUp: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.inputType === self.inputTypeEnum.mouse)
                    {
                        self.inputEnd();
                        self.inputType = null;
                    }
                },

                onTouchEnd: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.inputType === self.inputTypeEnum.touch)
                    {
                        self.inputEnd();
                        self.inputType = null;
                    }
                },
                onTouchMove: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.inputType === self.inputTypeEnum.touch)
                    {
                        evt.preventDefault();
                        self.inputMove($$(evt.currentTarget), self.getCoords(self.inputType, evt));
                    }
                }
            };
        },
        classCreated: (namespace, classConstructor) =>
        {
            window.dragDropManager = new classConstructor();
        }
    });

    // #endregion

    // #region Launch Application Event

    (() =>
    {
        var
            deviceReady = false,
            documentReady = false;

        function checkReady()
        {
            if (deviceReady && documentReady)
            {
                setTimeout(() =>
                {
                    $$(document).trigger("launchapplication");
                });
            }
        }

        $$(() => { documentReady = true; checkReady(); });
        $$(document).on("deviceready", () => { deviceReady = true; checkReady(); });

    })();

    if (!window.cordova)
    {
        $$(() =>
        {
            setTimeout(() =>
            {
                $$(document).trigger("deviceready");
            });
        });
    }

    // #endregion

    // #region Application

    classFactory.create({
        namespace: "seriouslyrad.ui",
        name: "Application",
        properties: [
            { name: "authenticated", type: "boolean" },
            { name: "authenticationScheme", type: "string" },
            { name: "authenticationData" },
            { name: "firstMenuItemIsHomePage", type: "boolean" },
            { name: "loginWithDialog", type: "boolean" }
        ],
        createPropertyDefaults: namespace =>
        {
            return {
                authenticationScheme: "Cookies",
                loginWithDialog: true
            };
        },
        createConstructor: namespace =>
        {
            return function ()
            {
                var
                    self = this;

                if (window.application)
                    console.error("Application has already been instantiated!");

                window.application = self;
                self.triggeredReady = false;
                self.onAuthenticatedAsyncCalled = false;
                self.checkLoginIntervalId = 0;

                if (window.applicationInformation.authenticationData)
                    self.authenticationDataSet(window.atob(window.applicationInformation.authenticationData).fromJson());

                self._preInitializationAsync()
                    .then(() =>
                    {
                        self._setDevice();
                        self._bindEvents();
                        self._setupMainMenu();
                        self._setupWebClients();
                        self._setupPermissionCache();
                        self._setupPageManager();
                        self._setupUserAccountDialog();
                        self._setupUserMenu();

                        self._triggerReady();

                        return self._postInitializationAsync();
                    });

                // Trigger Property Changes
                this.loginWithDialogTriggerChange();
            };
        },
        createPrototype: namespace =>
        {
            return {
                // "Protected" Methods
                _authenticatedChanged: function (change)
                {
                    if (change.currentValue)
                    {
                        $$("html").addClass("rad-state-authenticated");
                        $$("html").removeClass("rad-state-unauthenticated");
                    }
                    else
                    {
                        $$("html").removeClass("rad-state-authenticated");
                        $$("html").addClass("rad-state-unauthenticated");
                    }
                },
                _bindEvents: function ()
                {
                    $$(document).on("seriouslyrad.ui.NotificationHub:UserSettingsChanged", { self: this }, this.onUserSettingsChanged);
                    $$(document).on("seriouslyrad.server.AjaxRequest:ResponseMessagesReceived", { self: this }, this.onAjaxResponseMessagesReceived);
                    $$(document).on("seriouslyrad.ui.Application:Authenticated", { self: this }, this.onAuthenticated);
                    $$(document).on("seriouslyrad.ui.Application:Unauthenticated", { self: this }, this.onUnauthenticated);
                    $$(document).on("seriouslyrad.ui.PageManager:Navigated", { self: this }, this.onPageManagerNavigated);
                },
                _checkLoginAsync: function ()
                {
                    var
                        self = this;

                    return new Promise((resolve, reject) =>
                    {
                        var
                            showLoading = window.application ? !self.authenticatedGet() : false,
                            headers = self._createAuthenticationHeaders();

                        self.webApiClient
                            .createApiMethodRequest("Security", "CheckLogin", null, headers, showLoading)
                            .executeAsync()
                            .then(data =>
                            {
                                if (self.onAuthenticatedAsyncCalled)
                                    return;

                                return self._onAuthenticatedAsync(data);
                            })
                            .then(() =>
                            {
                                resolve();
                            })
                            .catch(error =>
                            {
                                self._onUnauthenticatedAsync()
                                    .finally(() =>
                                    {
                                        reject(error);
                                    });
                            })
                            .finally(() =>
                            {
                                self = null;
                            });
                    });
                },
                _createAuthenticationHeaders: function ()
                {
                    var
                        headers = {},
                        authenticationData = this.authenticationDataGet();

                    if (authenticationData)
                    {
                        switch (authenticationData.scheme)
                        {
                            case "Cookies":
                                headers["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                                break;
                            case "Bearer":
                                headers["Authorization"] = String.format("Bearer {0}", authenticationData.accessToken);
                                break;
                        }
                    }

                    return headers;
                },
                _getInitialPageUrl: function ()
                {
                    if (this.loadedInitialPage)
                        return null;

                    this.loadedInitialPage = true;

                    var
                        location = window.pathHelper.getLocation(window.location);

                    if ((location.applicationRelativeUrl !== String.empty && location.applicationRelativeUrl !== "index.html")) // index.html = Cordova)
                        return null;

                    if (!this.firstMenuItemIsHomePageGet())
                        return null;

                    if (!seriouslyrad.ui.web.MainMenu.instance)
                        return null;

                    return (
                        seriouslyrad.ui.web.MainMenu.instance.$$elementGet()
                            .find(".rad-control-button:not(.rad-state-hidden)")
                            .first()
                            .attr("href")
                    );
                },
                _getPlatform: function ()
                {
                    return window.device?.platform?.toLowerCase() || "browser";
                },
                _loadScriptAsync: function (src)
                {
                    var
                        promise = Promise.public();

                    if ($$("head script[src='" + src + "']").length > 0)
                        promise.resolve();
                    else
                    {
                        var
                            element = document.createElement("script");

                        element.setAttribute("type", "text/javascript");

                        $$("head").append(element);

                        element.onload = promise.resolve;
                        element.onerror = promise.reject;

                        element.setAttribute("src", src);
                    }

                    return promise;
                },
                _loadStyleSheetAsync: function (src)
                {
                    var
                        promise = Promise.public();

                    if ($$("head link[href='" + src + "']").length > 0)
                        promise.resolve();
                    else
                    {
                        var
                            element = document.createElement('link');

                        element.setAttribute("type", "text/css");
                        element.setAttribute("rel", "stylesheet");

                        $$("head").append(element);

                        element.onload = promise.resolve;
                        element.onerror = promise.reject;

                        element.setAttribute("href", src);
                    }

                    return promise;
                },
                _loadUserSettings: function ()
                {
                    /* Example
                     * 
                     
                    var
                        self = this;

                    self.webApiClient
                        .createEntitySearchRequest("UserSettings", "userSettingType/name eq 'Fix Footer'")
                        .executeAsync()
                        .then(data =>
                        {
                            
                        });

                    */
                },
                _loginWithDialogChanged: function (change)
                {
                    if (change.currentValue)
                    {
                        $$("html").removeClass("rad-feature-login-page");
                        $$("html").addClass("rad-feature-login-dialog");
                    }
                    else
                    {
                        $$("html").addClass("rad-feature-login-page");
                        $$("html").removeClass("rad-feature-login-dialog");
                    }
                },
                _onAuthenticatedAsync: function (data)
                {
                    if (this.authenticatedGet() === true)
                        return Promise.resolve();

                    var
                        self = this;

                    self.authenticatedSet(true);

                    if (self._onAuthenticatedAsyncPromise)
                        return self._onAuthenticatedAsyncPromise;

                    self._onAuthenticatedAsyncPromise = Promise.public();

                    if (window.fullPageLoader)
                        window.fullPageLoader.enqueue();

                    self.permissionCache.refresh()
                        .then(() =>
                        {
                            self._startCheckLogin();
                            self._loadUserSettings();
                            self._triggerAuthenticated();

                            var
                                initialPageUrl = self._getInitialPageUrl();

                            if (initialPageUrl)
                                window.pageManager.navigateAsync(initialPageUrl, true);
                            else if (self.loginWithDialogGet())
                                window.pageManager.reloadCurrentPageAsync();

                            self._onAuthenticatedAsyncPromise.resolve();
                            self._onAuthenticatedAsyncPromise = null;
                            self = null;
                        })
                        .catch(() =>
                        {
                            self._onUnauthenticatedAsync()
                                .finally(() =>
                                {
                                    self._onAuthenticatedAsyncPromise.reject();
                                    self._onAuthenticatedAsyncPromise = null;
                                    self = null;
                                });
                        })
                        .finally(() =>
                        {
                            if (window.fullPageLoader)
                                window.fullPageLoader.dequeue();
                        });

                    self.onAuthenticatedAsyncCalled = true;

                    return self._onAuthenticatedAsyncPromise;
                },
                _onRequestAuthenticationData: function ()
                {
                    return this.authenticationDataGet() || { scheme: this.authenticationSchemeGet() };
                },
                _onUnauthenticatedAsync: function ()
                {
                    var
                        self = this;

                    if (self.authenticatedGet() === false)
                        return Promise.resolve();

                    self.authenticatedSet(false);

                    self._stopCheckLogin();
                    window.pageManager.clear();
                    self._triggerUnauthenticated();
                    self.onAuthenticatedAsyncCalled = false;

                    const
                        authenticationData = this.authenticationDataGet();

                    if (authenticationData?.scheme === "Bearer" && authenticationData.accessToken && authenticationData.refreshToken)
                    {
                        return new Promise((resolve, reject) =>
                        {
                            $$
                                .ajax({
                                    type: "POST",
                                    headers: Object.extend(true, {}, seriouslyrad.server.WebApiClient.staticHeaders),
                                    dataType: "json",
                                    contentType: "application/json",
                                    url: String.format("{0}api/Security/{1}", window.applicationInformation.serverVirtualDirectory, "RefreshToken"),
                                    data: {
                                        accessToken: authenticationData.accessToken,
                                        refreshToken: authenticationData.refreshToken
                                    }.toJson(),
                                    xhrFields: {
                                        withCredentials: true
                                    }
                                })
                                .then(results =>
                                {
                                    const
                                        data = results[0];

                                    authenticationData.accessToken = data?.accessToken;
                                    authenticationData.refreshToken = data?.refreshToken;

                                    // Need to revisit this
                                    seriouslyrad.server.AjaxRequest.unauthenticated = false;

                                    seriouslyrad.server.AjaxRequest.processCallQueue();

                                    return self._onAuthenticatedAsync();
                                })
                                .then(() => { resolve(); })
                                .catch(_ =>
                                {
                                    this._requestLoginAsync()
                                        .then(() => resolve())
                                        .catch(() => reject());
                                })
                        });
                    }

                    return this._requestLoginAsync();
                },
                _postInitializationAsync: function ()
                {
                    return Promise.resolve();
                },
                _preInitializationAsync: function ()
                {
                    return Promise.resolve();
                },
                _requestLoginAsync: function ()
                {
                    var
                        self = this,
                        promise = new Promise((resolve, reject) =>
                        {
                            if (self.loginWithDialogGet())
                            {
                                window.pageManager.openPageAsDialogAsync("/login/")
                                    .then(result =>
                                    {
                                        if (result[0] !== namespace.web.Dialog.dialogResult.ok)
                                        {
                                            reject();
                                            return;
                                        }

                                        resolve(result[1]);
                                    })
                                    .catch(error =>
                                    {
                                        reject(error);
                                    });
                            }
                            else
                            {
                                setTimeout(() =>
                                {
                                    window.pageManager.clearHistoryAsync()
                                        .finally(() =>
                                        {
                                            window.pageManager
                                                .navigateAsync("/login/", true, false, null);
                                        });
                                });
                            }
                        });

                    return (
                        promise
                            .then(_ =>
                            {
                                seriouslyrad.server.AjaxRequest.processCallQueue(self.authenticationDataGet()?.antiForgeryToken);
                                return self._onAuthenticatedAsync();
                            })
                    );
                },
                _setDevice: function ()
                {
                    $$("html").addClass("rad-device-" + this._getPlatform());
                },
                _setupMainMenu: function ()
                {
                    var
                        $$mainMenu = $$(".rad-control-main-menu");

                    if ($$mainMenu.length === 0)
                        return;

                    if ($$mainMenu.length > 1)
                        console.error("Only one main menu is supported.");

                    seriouslyrad.ui.web.MainMenu.instance = seriouslyrad.ui.web.Control.createFromElement($$mainMenu);
                },
                _setupPageManager: function ()
                {
                    window.pageManager = new seriouslyrad.ui.PageManager({
                        onAuthenticated: () => this._onAuthenticatedAsync(),
                        onRequestAuthenticationData: () => this._onRequestAuthenticationData(),
                        onUnauthenticated: () => this._onUnauthenticatedAsync()
                    });
                },
                _setupPermissionCache: function ()
                {
                    this.permissionCache = new seriouslyrad.PermissionCache({ webApiClient: this.webApiClient });
                },
                _setupUserAccountDialog: function ()
                {
                    this.userAccountDialog = new seriouslyrad.ui.web.UserAccountDialog();
                },
                _setupUserMenu: function ()
                {
                    var
                        self = this;

                    self.userMenu =
                        new seriouslyrad.ui.web.Menu({ text: "User Menu" })
                            .append(new seriouslyrad.ui.web.MenuItem({ text: "My Account" }))
                            .append(new seriouslyrad.ui.web.MenuItem({ text: "Logout" }));

                    $$(document).on("click", ".rad-action-user-menu-open", evt =>
                    {
                        self.userMenu.openAsync()
                            .then(menuItem =>
                            {
                                if (!menuItem)
                                    return;

                                switch (menuItem.textGet())
                                {
                                    case "My Account":
                                        self.userAccountDialog.openAsync();
                                        break;
                                    case "Logout":
                                        self.logoutAsync();
                                        break;
                                }
                            });
                    });
                },
                _setupWebClients: function ()
                {
                    this.webClient = new seriouslyrad.server.WebClient({
                        onRequestAuthenticationData: () => this._onRequestAuthenticationData()
                    });

                    this.webApiClient = new seriouslyrad.server.WebApiClient({
                        onAuthenticatedAsync: (data) => this._onAuthenticatedAsync(data),
                        onConnected: () => this._triggerConnected(),
                        onDisconnected: () => this._triggerDisconnected(),
                        onRequestAuthenticationData: () => this._onRequestAuthenticationData(),
                        onUnauthenticated: () => this._onUnauthenticatedAsync()
                    });
                },
                _startCheckLogin: function ()
                {
                    var
                        self = this;

                    self._stopCheckLogin();

                    this.checkLoginIntervalId =
                        setInterval(() => { self._checkLoginAsync(); }, 30 * 1000);
                },
                _stopCheckLogin: function ()
                {
                    if (this.checkLoginIntervalId)
                    {
                        clearInterval(this.checkLoginIntervalId);
                        this.checkLoginIntervalId = 0;
                    }
                },
                _triggerAuthenticated: function ()
                {
                    $$(document).trigger("seriouslyrad.ui.Application:Authenticated", this);
                },
                _triggerConnected: function ()
                {
                    $$(document).trigger("seriouslyrad.ui.Application:Connected", this);
                },
                _triggerDisconnected: function ()
                {
                    $$(document).trigger("seriouslyrad.ui.Application:Disconnected", this);
                },
                _triggerReady: function ()
                {
                    if (this.triggeredReady)
                        return;

                    this.triggeredReady = true;
                    $$(document).trigger("seriouslyrad.ui.Application:Ready", this);
                },
                _triggerUnauthenticated: function ()
                {
                    $$(document).trigger("seriouslyrad.ui.Application:Unauthenticated", this);
                },

                // Public Methods
                logoutAsync: function ()
                {
                    var
                        self = this;

                    self.authenticationDataSet(null);

                    return (
                        self.webApiClient
                            .createApiMethodRequest("Security", "Logout")
                            .executeAsync()
                            .then(() =>
                            {
                                self.permissionCache.clear();
                                self._checkLoginAsync();
                                self = null;
                            })
                    );
                },
                navigationPanelClose: function ()
                {
                    this.navigationPanel.close();
                },
                navigationPanelOpen: function ()
                {
                    this.navigationPanel.open();
                },

                // Event Handlers
                onAuthenticated: function (evt)
                {
                    /* Intentionally empty */
                },
                onAjaxResponseMessagesReceived: function (evt, eventData)
                {
                    seriouslyrad.ui.web.AjaxResponseMessageDialog.displayMessages(eventData.responseMessages);
                },
                onPageManagerNavigated: function (evt)
                {
                    var
                        self = evt.data.self;

                    if (self.initialized)
                        return;

                    $$("html").removeClass("rad-state-initializing");

                    self.initialized = true;
                },
                onUnauthenticated: function (evt)
                {
                    /* Intentionally empty */
                },
                onUserSettingsChanged: function (evt)
                {
                    evt.data.self._loadUserSettings();
                }
            };
        }
    });

    // #endregion
